import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Search, Send } from "lucide-react";

/**
 * SparesDispatchDialog — opens for a complaint that is in "Parts Approved" status.
 *
 * Pre-fills complaint #, technician, requested part name/code/qty from the complaint.
 * Searches /parts for matching inventory rows by part_code → part_name. Spares Assistant
 * picks the actual stock row, picks warehouse (BRT/KTM), and the dialog shows live
 * available stock. Blocks dispatch if requested qty > available qty. Spare photo is
 * compulsory; courier details captured; POD is generated server-side, then dispatch
 * is confirmed in a second call.
 */
export default function SparesDispatchDialog({ open, onOpenChange, complaint, onDispatched }) {
  const [parts, setParts] = useState([]);
  const [partSearch, setPartSearch] = useState("");
  const [selectedPart, setSelectedPart] = useState(null);
  const [form, setForm] = useState(initialForm(complaint));
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initialForm(complaint));
    setSelectedPart(null);
    // Pre-search by the requested part code or name
    const req = complaint?.parts_request || {};
    const initialQ = (req.part_code || req.part_name || "").trim();
    setPartSearch(initialQ);
    api.get("/parts", { params: { condition: "Fresh" } }).then((r) => {
      setParts(r.data || []);
      // Auto-select if exactly one match by part_code or part_name (case-insensitive)
      const q = initialQ.toLowerCase();
      if (q) {
        const hits = (r.data || []).filter((p) =>
          (p.model_number || "").toLowerCase() === q
          || (p.part_code || "").toLowerCase() === q
          || (p.part_name || "").toLowerCase() === q);
        if (hits.length === 1) setSelectedPart(hits[0]);
      }
    }).catch(() => setParts([]));
  }, [open, complaint]);

  const filtered = useMemo(() => {
    const q = (partSearch || "").toLowerCase().trim();
    if (!q) return parts.slice(0, 8);
    return parts.filter((p) =>
      `${p.part_name || ""} ${p.brand || ""} ${p.product || ""} ${p.model_number || ""} ${p.part_code || ""}`
        .toLowerCase().includes(q),
    ).slice(0, 12);
  }, [parts, partSearch]);

  const warehouseField = form.from === "BRT" ? "qty_brt" : "qty_ktm";
  const available = selectedPart ? (selectedPart[warehouseField] || 0) : 0;
  const insufficient = selectedPart && Number(form.qty) > available;

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
  const onSparePhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Spare photo must be an image"); return; }
    if (f.size > 4_500_000) { toast.error("Spare photo too large (max ~4 MB)"); return; }
    const dataUrl = await readFileAsDataUrl(f);
    setForm((s) => ({ ...s, spare_photo_url: dataUrl }));
  };
  const onBillChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ok = f.type.startsWith("image/") || f.type === "application/pdf";
    if (!ok) { toast.error("Transport bill must be image or PDF"); return; }
    if (f.size > 6_000_000) { toast.error("Transport bill too large (max ~6 MB)"); return; }
    const dataUrl = await readFileAsDataUrl(f);
    setForm((s) => ({ ...s, transport_bill_url: dataUrl }));
  };

  const generatePOD = async () => {
    if (!selectedPart) { toast.error("Pick a stock part first"); return; }
    if (!form.spare_photo_url) { toast.error("Spare part photo is required"); return; }
    if (!form.qty || form.qty <= 0) { toast.error("Quantity must be > 0"); return; }
    if (insufficient) { toast.error(`Insufficient stock at ${form.from} — only ${available} available`); return; }
    if (!form.courier_company.trim()) { toast.error("Courier company is required for POD"); return; }
    if (!form.docket_number.trim()) { toast.error("Docket / consignment number is required for POD"); return; }
    if (!form.sent_to.trim()) { toast.error("Sent To (technician/store) is required"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/spares/pod", {
        complaint_id: complaint.id,
        part_id: selectedPart.id,
        part_name: selectedPart.part_name,
        part_code: selectedPart.part_code || selectedPart.model_number,
        quantity: Number(form.qty),
        spare_photo_url: form.spare_photo_url,
        transport_bill_url: form.transport_bill_url || undefined,
        courier_company: form.courier_company,
        docket_number: form.docket_number,
        dispatch_date_input: form.dispatch_date_input,
        expected_delivery_date: form.expected_delivery_date,
        freight_charge: form.freight_charge !== "" ? Number(form.freight_charge) : undefined,
        sent_from: form.sent_from,
        sent_to: form.sent_to,
        receiver_name: form.receiver_name,
        courier_remarks: form.courier_remarks,
      });
      setForm((f) => ({ ...f, pod_number: data.pod_number }));
      toast.success("POD generated", { description: data.pod_number });
    } catch (e) {
      toast.error("POD generation failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setGenerating(false); }
  };

  const submitDispatch = async () => {
    if (!form.pod_number) { toast.error("Generate POD first"); return; }
    if (!form.spare_photo_url) { toast.error("Spare photo is required"); return; }
    if (insufficient) { toast.error("Insufficient stock"); return; }
    setSubmitting(true);
    try {
      await api.post(`/parts/${selectedPart.id}/dispatch`, {
        qty: Number(form.qty),
        from: form.from,
        complaint_id: complaint.id,
        courier: form.courier_company,
        pod_number: form.pod_number,
      });
      toast.success("Dispatched to technician");
      onOpenChange(false);
      onDispatched?.();
    } catch (e) {
      toast.error("Dispatch failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setSubmitting(false); }
  };

  if (!complaint) return null;
  const req = complaint.parts_request || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="spares-dispatch-dialog">
        <DialogHeader>
          <DialogTitle>Dispatch Spare Part · {complaint.complaint_number}</DialogTitle>
          <DialogDescription>
            Approved part request — select the actual stock row, fill POD content and courier details, then dispatch to the technician.
          </DialogDescription>
        </DialogHeader>

        {/* Approved request summary (read-only) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
          <Info label="Complaint #" value={complaint.complaint_number} />
          <Info label="Customer" value={complaint.customer_name} />
          <Info label="Technician" value={complaint.technician_name || "—"} />
          <Info label="Approved by" value={complaint.parts_approved_by || "—"} />
          <Info label="Requested Part" value={req.part_name || complaint.parts_required || "—"} />
          <Info label="Part Code" value={req.part_code || "—"} mono />
          <Info label="Requested Qty" value={req.quantity || 1} />
          <Info label="Urgency" value={(req.urgency || "normal").toUpperCase()} />
        </div>

        {/* Pick from stock */}
        <div className="rounded-md border border-slate-200 p-3 mt-1">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">Select Stock Part *</div>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
              <Input className="pl-7 h-8 max-w-xs" placeholder="Search inventory by code/name…"
                     value={partSearch} onChange={(e) => setPartSearch(e.target.value)}
                     data-testid="dispatch-part-search" />
            </div>
          </div>
          <div className="border rounded max-h-44 overflow-y-auto" data-testid="dispatch-part-options">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-slate-400">
                No matching parts in inventory. Add stock via Spares Parts Entry first.
              </div>
            )}
            {filtered.map((p) => {
              const isSel = selectedPart?.id === p.id;
              const total = (p.qty_brt || 0) + (p.qty_ktm || 0);
              return (
                <button key={p.id} type="button"
                        onClick={() => setSelectedPart(p)}
                        className={`w-full text-left px-3 py-2 text-xs border-b last:border-b-0 hover:bg-slate-50 flex items-center justify-between gap-3 ${isSel ? "bg-emerald-50" : ""}`}
                        data-testid={`dispatch-part-opt-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.part_name}</div>
                    <div className="text-slate-500 font-mono truncate">
                      {p.part_code || p.model_number || "—"} · {p.brand || ""} {p.product || ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono">BRT {p.qty_brt || 0} · KTM {p.qty_ktm || 0}</div>
                    <div className={`text-[10px] font-semibold ${total > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {total > 0 ? `${total} in stock` : "OUT OF STOCK"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Stock & quantity */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Fld label="From Warehouse">
            <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm"
                    value={form.from}
                    onChange={(e) => setForm({ ...form, from: e.target.value })}
                    data-testid="dispatch-warehouse-select">
              <option value="KTM">KTM (Kathmandu)</option>
              <option value="BRT">BRT</option>
            </select>
          </Fld>
          <Fld label="Quantity to Dispatch *">
            <Input type="number" min={1} value={form.qty}
                   onChange={(e) => setForm({ ...form, qty: parseInt(e.target.value) || 1 })}
                   data-testid="dispatch-qty-input" />
          </Fld>
          <Fld label="Available Stock">
            {selectedPart ? (
              <div className={`h-9 px-2 flex items-center text-sm font-mono rounded border ${insufficient ? "border-rose-300 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}
                   data-testid="dispatch-available-stock">
                {available} @ {form.from}
                {insufficient && <AlertTriangle size={13} className="ml-1" />}
              </div>
            ) : (
              <div className="h-9 px-2 flex items-center text-sm text-slate-400">Pick a part</div>
            )}
          </Fld>
        </div>

        {/* Spare photo - mandatory */}
        <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-rose-700 mb-2">
            Spare Part Photo · Required *
          </div>
          <Input type="file" accept="image/*" onChange={onSparePhotoChange} data-testid="dispatch-spare-photo-input" />
          {form.spare_photo_url && (
            <div className="mt-2 flex items-center gap-3">
              <img src={form.spare_photo_url} alt="spare" className="h-16 w-16 object-cover rounded border border-slate-200" />
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, spare_photo_url: "" })}>Remove</Button>
            </div>
          )}
        </div>

        {/* Courier details */}
        <div className="rounded-md border border-slate-200 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
            POD / Courier Details · fill manually
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Fld label="Courier Company *">
              <Input value={form.courier_company} onChange={(e) => setForm({ ...form, courier_company: e.target.value })}
                     data-testid="dispatch-courier-company" placeholder="e.g. Nepal Logistics" />
            </Fld>
            <Fld label="Docket / Consignment # *">
              <Input value={form.docket_number} onChange={(e) => setForm({ ...form, docket_number: e.target.value })}
                     data-testid="dispatch-docket-number" />
            </Fld>
            <Fld label="Dispatch Date">
              <Input type="date" value={form.dispatch_date_input}
                     onChange={(e) => setForm({ ...form, dispatch_date_input: e.target.value })}
                     data-testid="dispatch-date-input" />
            </Fld>
            <Fld label="Expected Delivery">
              <Input type="date" value={form.expected_delivery_date}
                     onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })}
                     data-testid="dispatch-expected-input" />
            </Fld>
            <Fld label="Freight (NPR)">
              <Input type="number" min={0} value={form.freight_charge}
                     onChange={(e) => setForm({ ...form, freight_charge: e.target.value })}
                     data-testid="dispatch-freight-input" />
            </Fld>
            <Fld label="Sent From">
              <Input value={form.sent_from} onChange={(e) => setForm({ ...form, sent_from: e.target.value })}
                     data-testid="dispatch-sent-from" placeholder="e.g. KTM Warehouse" />
            </Fld>
            <Fld label="Sent To (Technician / Store) *">
              <Input value={form.sent_to} onChange={(e) => setForm({ ...form, sent_to: e.target.value })}
                     data-testid="dispatch-sent-to"
                     placeholder={complaint.technician_name || "Receiver name / location"} />
            </Fld>
            <Fld label="Receiver Name">
              <Input value={form.receiver_name} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })}
                     data-testid="dispatch-receiver-name" />
            </Fld>
            <Fld label="Remarks">
              <Input value={form.courier_remarks} onChange={(e) => setForm({ ...form, courier_remarks: e.target.value })}
                     data-testid="dispatch-courier-remarks" />
            </Fld>
          </div>
        </div>

        {/* Transport bill optional */}
        <div className="rounded-md border border-slate-200 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
            Transport / Courier Bill · optional (can be added later)
          </div>
          <Input type="file" accept="image/*,application/pdf" onChange={onBillChange} data-testid="dispatch-bill-input" />
          {form.transport_bill_url && (
            <div className="mt-2 text-xs text-emerald-700 flex items-center justify-between">
              <span>Bill attached ({form.transport_bill_url.startsWith("data:application/pdf") ? "PDF" : "Image"})</span>
              <Button size="sm" variant="outline" onClick={() => setForm({ ...form, transport_bill_url: "" })}>Remove</Button>
            </div>
          )}
        </div>

        {/* POD */}
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">POD (Proof of Dispatch)</div>
          {!form.pod_number ? (
            <>
              <div className="text-xs text-amber-800 mb-2">POD will be auto-numbered. Spare photo + courier details must be filled first.</div>
              <Button size="sm" onClick={generatePOD}
                      disabled={generating || !selectedPart || !form.spare_photo_url || insufficient}
                      className="bg-amber-600 text-white hover:bg-amber-700"
                      data-testid="dispatch-generate-pod">
                {generating ? "Generating…" : "Generate POD"}
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2 text-emerald-800 text-sm">
              <CheckCircle2 size={16} />
              POD <span className="font-mono font-semibold">{form.pod_number}</span> generated.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submitDispatch}
                  disabled={submitting || !form.pod_number || !form.spare_photo_url || insufficient || !selectedPart}
                  className="bg-slate-900 text-white disabled:opacity-50"
                  data-testid="dispatch-confirm-btn">
            <Send size={14} className="mr-1" /> {submitting ? "Dispatching…" : "Confirm Dispatch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function initialForm(complaint) {
  const req = complaint?.parts_request || {};
  return {
    qty: req.quantity || 1,
    from: "KTM",
    courier_company: "", docket_number: "", dispatch_date_input: "",
    expected_delivery_date: "", freight_charge: "",
    sent_from: "", sent_to: complaint?.technician_name || "",
    receiver_name: "", courier_remarks: "",
    spare_photo_url: "", transport_bill_url: "",
    pod_number: "",
  };
}

function Fld({ label, children }) {
  return <div><Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>{children}</div>;
}
function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm text-slate-800 truncate ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
